import React, { FunctionComponent, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import classnames from 'classnames';
import withStyles from 'react-jss';

import { theme } from 'config';
import { ClassProp } from 'helpers/types';
import FormError from '../FormError';

import Eye from 'assets/icons/Password/eye.svg';
import EyeOpen from 'assets/icons/Password/eye-open.svg';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  name: string;
  placeholder: string;
  label: string;
  size?: string;
  tabIndex?: number;
  disabled?: boolean;
  autoComplete?: string;
  register: any;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const styles = {
  container: {
    width: '100%',
    marginTop: '.25rem !important',
  },
  small: {
    width: '31%',
    marginRight: '.55rem',
  },
  medium: {
    width: '48%',
    marginRight: '.55rem',
  },
  large: {
    width: '100%',
  },
  fieldLabel: {
    color: '#646464 !important',
    fontSize: '.875rem !important',
    fontWeight: '500 !important',
    margin: '0 0 .30rem 0',
    display: 'block !important',
  },
  fieldInput: {
    display: 'flex',
    flex: '1 0 auto',
    textAlign: 'left',
    margin: '0',
    outline: '0',
    tapHighlightColor: 'rgba(255,255,255,0)',
    lineHeight: '1.21428571rem !important',
    fontSize: '1rem !important',
    background: '#fff',
    color: 'rgba(0,0,0,.87)',
    boxShadow: '0 0 0 0 transparent inset',
    transition: 'color .1s ease,border-color .1s ease',
  },
  disabled: {
    background: '#e1e8ed',
  },
  passwordReveal: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
  },
  passwordContainer: {
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '100%',
    width: '100%',
    border: '1px solid rgba(34,36,38,.15)',
    borderRadius: '.28571429rem',
  },
  "@global .ui.form input[type='password']": {
    flex: '1 0 auto',
    width: 'unset',
    outline: 0,
    border: 'none !important',
    textAlign: 'left',
    backgroundColor: 'transparent !important',
    transition:
      'box-shadow .1s ease,border-color .1s ease,-webkit-box-shadow .1s ease',
    boxShadow: 'none',
    '::-ms-reveal': {
      display: 'none',
    },
  },
  textMode: {
    flex: '1 0 auto',
    outline: 0,
    width: 'unset !important',
    border: 'none !important',
    textAlign: 'left',
    backgroundColor: 'transparent !important',
    transition:
      'box-shadow .1s ease,border-color .1s ease,-webkit-box-shadow .1s ease',
    boxShadow: 'none',
  },
  icon: {
    width: '1em',
    height: '1em',
  },
  'input::-ms-reveal': {
    display: 'none',
  },
  error: theme.error,
};

const PasswordInput: FunctionComponent<Props> = ({
  classes,
  label,
  name,
  size,
  disabled = false,
  onChange,
  autoComplete,
  register,
  ...rest
}) => {
  const { errors, setValue, trigger, clearErrors } = useFormContext();

  const [isPasswordVisible, setPasswordVisible] = useState(false);

  const onBlurHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setValue(name, value);
    if (value !== '' && !isPasswordVisible) {
      trigger(name);
    }
  };

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setValue(name, value);
    if (value === '') {
      clearErrors(name);
    }
  };

  const switchPwdVisibility = () => {
    setPasswordVisible(!isPasswordVisible);
  };

  const fieldErrors = errors[name];

  return (
    <div
      className={classnames(classes.container, {
        [classes.small]: size === 'small',
        [classes.medium]: size === 'medium',
        [classes.large]: size === 'large',
      })}
    >
      <label htmlFor={name} />
      <span className={classes.fieldLabel}>{label}</span>
      <div
        className={classnames(classes.passwordContainer, {
          [classes.error]: fieldErrors,
        })}
      >
        <input
          className={classnames(classes.fieldInput, {
            [classes.textMode]: isPasswordVisible,
          })}
          id={name}
          name={name}
          type={isPasswordVisible ? 'text' : 'password'}
          onBlur={onBlurHandler}
          onChange={onChangeHandler}
          disabled={disabled}
          autoComplete={autoComplete}
          ref={register}
          {...rest}
        />
        <button
          type="button"
          onMouseDown={switchPwdVisibility}
          className={classes.passwordReveal}
          tabIndex={-1}
        >
          <img
            className={classes.icon}
            src={isPasswordVisible ? Eye : EyeOpen}
            alt={!isPasswordVisible ? 'show password' : 'hide password'}
          />
        </button>
      </div>
      {fieldErrors && <FormError name={name} errors={errors} />}
    </div>
  );
};

export default withStyles(styles)(PasswordInput);
